import React, { useEffect, useMemo, useState } from 'react'

import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import {
    Assignment as GuestCardIcon,
    Close as CloseIcon,
} from '@material-ui/icons'

import { useGuestCards } from '../../hooks/Outreach/useGuestCards'
import { GuestCardFormRequest } from '../../hooks/Outreach/useGuestCardForm'
import { useSubmitLeadGuestCard } from '../../hooks/Outreach/useSubmitLeadGuestCard'
import { useAptConfig } from '../../hooks/useAptConfig'
import {
    ApartmentTourGuestCardResults,
    ApartmentTourLead,
    GuestCard,
} from '../../models/Outreach/types'
import { GuestCardFormFields } from '../Outbound/Leads/ProspectsTab/GuestCardFormFields'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: 16,
            width: 'min(720px, 92vw)',
            height: 'min(860px, 88vh)',
            maxWidth: '92vw',
            maxHeight: '88vh',
            display: 'flex',
            flexDirection: 'column',
            margin: theme.spacing(2),
        },
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: theme.spacing(2.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#f9fafb',
        flexShrink: 0,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    titleIcon: {
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: '#ede9fe',
        color: '#7c3aed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
        marginTop: 4,
        marginLeft: 52,
    },
    dialogContent: {
        padding: theme.spacing(2.5, 3, 3),
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
    },
    pickerCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
        backgroundColor: '#fafafa',
        position: 'sticky' as const,
        top: 0,
        zIndex: 1,
    },
    formControl: {
        minWidth: '100%',
        marginTop: theme.spacing(1.25),
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#fff',
        },
    },
    empty: {
        textAlign: 'center',
        padding: theme.spacing(4, 2),
        color: theme.palette.text.secondary,
    },
    formHint: {
        textAlign: 'center',
        padding: theme.spacing(5, 2),
        color: theme.palette.text.secondary,
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: 12,
    },
}))

interface SubmitLeadGuestCardDialogProps {
    open: boolean
    onClose: () => void
    lead: ApartmentTourLead | null
    onSuccess?: (
        results: ApartmentTourGuestCardResults,
        contact: {
            name?: string
            email?: string
            phone?: string
        },
    ) => void | Promise<void>
}

export const SubmitLeadGuestCardDialog: React.FC<SubmitLeadGuestCardDialogProps> = ({
    open,
    onClose,
    lead,
    onSuccess,
}) => {
    const classes = useStyles()
    const { guestCards, isLoadingGuestCards } = useGuestCards()
    const { unitConfigList } = useAptConfig({ unitConfigList: true })
    const {
        submitLeadGuestCard,
        isSubmitting,
        reset,
    } = useSubmitLeadGuestCard()

    const [selectedGuestCardId, setSelectedGuestCardId] = useState<number | ''>(
        '',
    )
    const [formKey, setFormKey] = useState(0)

    const activeGuestCards = useMemo(
        () => (guestCards ?? []).filter((card) => !card.archived),
        [guestCards],
    )

    const selectedGuestCard: GuestCard | null = useMemo(() => {
        if (selectedGuestCardId === '') return null
        return (
            activeGuestCards.find((c) => c.id === selectedGuestCardId) ?? null
        )
    }, [activeGuestCards, selectedGuestCardId])

    const guestCardFormData = useMemo(() => {
        if (!selectedGuestCard) return null
        const unitConfigs = (
            unitConfigList.filter((config) => config.shows_on_schedule) || []
        ).map((config) => ({
            ...config,
            bed_count:
                (config as { bed_count?: number | null }).bed_count ?? null,
            bath_count:
                (config as { bath_count?: number | null }).bath_count ?? null,
        }))
        return {
            guest_card: selectedGuestCard,
            name: lead?.name ?? null,
            email: lead?.email ?? null,
            phone: lead?.phone ?? null,
            unit_configs: unitConfigs,
            apartment_name: '',
        }
    }, [selectedGuestCard, unitConfigList, lead])

    useEffect(() => {
        if (!open) return
        setSelectedGuestCardId('')
        setFormKey((k) => k + 1)
        reset()
    }, [open, lead?.id, reset])

    useEffect(() => {
        if (!open || selectedGuestCardId !== '') return
        if (activeGuestCards.length === 1) {
            setSelectedGuestCardId(activeGuestCards[0].id)
            return
        }
        const defaultCard = activeGuestCards.find((c) => c.default)
        if (defaultCard) {
            setSelectedGuestCardId(defaultCard.id)
        }
    }, [open, activeGuestCards, selectedGuestCardId])

    const handleGuestCardChange = (cardId: number) => {
        setSelectedGuestCardId(cardId)
        setFormKey((k) => k + 1)
    }

    const buildGuestCardResults = (
        formData: GuestCardFormRequest,
        guestCard: GuestCard,
    ): ApartmentTourGuestCardResults => {
        const unitConfigId = formData.floor_plan
        const unitConfig =
            unitConfigId != null
                ? unitConfigList.find((config) => config.id === unitConfigId)
                : null
        const rent =
            formData.preferred_rent_cost != null &&
            formData.preferred_rent_cost !== ''
                ? Number(formData.preferred_rent_cost)
                : null

        return {
            preferred_unit_config: unitConfig
                ? { id: unitConfig.id, name: unitConfig.name }
                : unitConfigId != null
                ? { id: unitConfigId, name: `Floor plan #${unitConfigId}` }
                : null,
            preferred_rent_cost:
                rent != null && !Number.isNaN(rent) ? rent : null,
            preferred_move_in_date: formData.preferred_move_in_date ?? null,
            additional_requests: formData.additional_requests ?? null,
            custom_answers: (formData.custom_questions ?? []).map((q) => {
                const questionText =
                    guestCard.card_questions.find(
                        (cq) => cq.custom_question.id === q.question,
                    )?.custom_question.question ?? `Question #${q.question}`
                return {
                    question_id: q.question,
                    question: questionText,
                    answer: q.answer,
                }
            }),
        }
    }

    const handleSubmit = async (formData: GuestCardFormRequest) => {
        if (!lead || !selectedGuestCard) return
        try {
            await submitLeadGuestCard({
                leadId: lead.id,
                payload: {
                    ...formData,
                    guest_card_id: selectedGuestCard.id,
                },
            })
            await onSuccess?.(
                buildGuestCardResults(formData, selectedGuestCard),
                {
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                },
            )
            onClose()
        } catch {
            // toast handled in hook
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth={false}
        >
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Box>
                    <Box className={classes.titleRow}>
                        <span className={classes.titleIcon}>
                            <GuestCardIcon style={{ fontSize: 22 }} />
                        </span>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                            Fill Guest Card
                        </Typography>
                    </Box>
                    <Typography className={classes.subtitle}>
                        {lead?.name
                            ? `For ${lead.name}`
                            : 'Complete guest card for this lead'}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {isLoadingGuestCards ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        py={6}
                    >
                        <CircularProgress size={28} />
                    </Box>
                ) : activeGuestCards.length === 0 ? (
                    <Box className={classes.empty}>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            No guest cards configured for this property
                        </Typography>
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ display: 'block', marginTop: 4 }}
                        >
                            Create a guest card template in Leasing Season
                            before filling one out.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box className={classes.pickerCard}>
                            <Typography
                                variant="subtitle2"
                                style={{ fontWeight: 700 }}
                            >
                                Guest card template
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginTop: 2 }}
                            >
                                Select a template to load its fields below.
                            </Typography>
                            <FormControl
                                variant="outlined"
                                size="small"
                                className={classes.formControl}
                            >
                                <InputLabel id="guest-card-select-label">
                                    Guest Card
                                </InputLabel>
                                <Select
                                    labelId="guest-card-select-label"
                                    label="Guest Card"
                                    value={selectedGuestCardId}
                                    onChange={(e) =>
                                        handleGuestCardChange(
                                            e.target.value as number,
                                        )
                                    }
                                    disabled={isSubmitting}
                                >
                                    {activeGuestCards.map((card) => (
                                        <MenuItem key={card.id} value={card.id}>
                                            {card.card_name}
                                            {card.default ? ' (default)' : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {guestCardFormData ? (
                            <GuestCardFormFields
                                key={formKey}
                                guestCardFormData={guestCardFormData}
                                prospect={null}
                                onSubmit={handleSubmit}
                                isLoading={isSubmitting}
                                submitLabel="Submit Guest Card"
                                loadingLabel="Submitting..."
                            />
                        ) : (
                            <Box className={classes.formHint}>
                                <Typography
                                    variant="body2"
                                    style={{ fontWeight: 500 }}
                                >
                                    Choose a guest card above to fill out the
                                    form
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}
